import { Case, Else, If, Switch, Then } from "react-if";

import type { NextPage } from "next";

import { Spinner } from "@src/packages/chime-ui";

import { useStateMachine } from "@src/hooks/useStateMachine";
import { useKYCVendorInformation, usePrivacyUserConsent } from "@src/queries/kycQueries";
import { Error } from "@src/views/Error";
import { Explainer } from "@src/views/Explainer";
import { PersonaDocumentCapture } from "@src/views/Persona/PersonaDocumentCapture";
import { IncodeDocumentCapture } from "@src/views/Incode";
import { usePageTracking } from "@src/hooks/usePageTracking";
import { isEmpty } from "lodash";
import { isValidChimeUrl } from "@src/utils/urlValidator";

const OriginalComponent: React.FC<{
  onComplete: (result: string) => void;
}> = ({ onComplete }) => {
  const { state, send } = useStateMachine();
  const { externalId, vendor, vendorToken, userId } = state.context;
  const { data, loading, error } = usePrivacyUserConsent(externalId);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <Error />;
  }

  const showConsent = data?.unauthenticated?.scan_id?.consent?.show_consent;
  const privacyNotice = data?.unauthenticated?.scan_id?.consent?.notice;

  return (
    <Switch>
      <Case condition={state.matches("loading")}>
        <Spinner />
      </Case>
      <Case condition={state.matches("error")}>
        <Error />
      </Case>
      <Case condition={state.matches("explainer")}>
        <Explainer
          onSubmit={() => send({ type: "EXPLAINER_CLICKED" })}
          showConsent={showConsent}
          privacyNotice={privacyNotice}
        />
      </Case>
      <Case condition={state.matches("scan_id")}>
        <If condition={vendor === "incode"}>
          <Then>
            <IncodeDocumentCapture
              interviewId={externalId}
              vendorToken={vendorToken}
              userId={userId}
              onComplete={onComplete}
            />
          </Then>
          <Else>
            <PersonaDocumentCapture inquiryId={externalId} onComplete={onComplete} />
          </Else>
        </If>
      </Case>
    </Switch>
  );
};

const NewWithKYCDataComponent: React.FC<{
  onComplete: (result: string) => void;
}> = ({ onComplete }) => {
  const { state, send } = useStateMachine();
  const { vendorInquiryId } = state.context;

  const { data, loading, error } = useKYCVendorInformation(vendorInquiryId);
  const vendor_inquiry = data?.unauthenticated?.kyc?.vendor_inquiry;

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <Error />;
  }

  return (
    vendor_inquiry?.__typename === "VendorInquiryResult" && (
      <Switch>
        <Case condition={state.matches("explainer")}>
          <Explainer onSubmit={() => send({ type: "EXPLAINER_CLICKED" })} />
        </Case>
        <Case condition={state.matches("scan_id")}>
          <If condition={vendor_inquiry?.vendor === "INCODE" ? "incode" : "persona"}>
            <Then>
              <IncodeDocumentCapture
                interviewId={vendor_inquiry?.vendor_id}
                vendorToken={vendor_inquiry?.vendor_token}
                userId={vendor_inquiry?.user_id}
                onComplete={onComplete}
              />
            </Then>
            <Else>
              <PersonaDocumentCapture inquiryId={vendor_inquiry?.id} onComplete={onComplete} />
            </Else>
          </If>
        </Case>
      </Switch>
    )
  );
};

const ScanIdPage: NextPage = () => {
  usePageTracking("index");
  const { state } = useStateMachine();
  const { ic: identityClient, callback } = state.context;

  const onComplete = (result = "") => {
    const url = new URL(callback);

    if (!isEmpty(result)) {
      url.searchParams.append(result, "true");
    }

    window.location.href = url.toString();
  };

  if (callback !== undefined && !isValidChimeUrl(callback)) {
    return <Error />;
  }

  return (
    <>
      {identityClient ? (
        <NewWithKYCDataComponent onComplete={onComplete} />
      ) : (
        <OriginalComponent onComplete={onComplete} />
      )}
    </>
  );
};

export default ScanIdPage;

export async function getStaticProps() {
  return {
    props: {
      messages: (await import(`../../locales/en-US.json`)).default,
    },
  };
}
